import React, { useState } from 'react';
import { Card, CardContent as CardBody, CardDescription } from '@shadcn/components/ui/card';
import { Col } from '@shadcn/components/ui/col';
import { Row } from '@shadcn/components/ui/row';
import { Button } from '@shadcn/components/ui/button';
import { backendUrl } from '@helpers/consts';
import { CSVLink } from 'react-csv';
import Select from '@shadcn/components/ui/react-select';

import Breadcrumb from '../../../components/Common/Breadcrumb';
import superagent from 'superagent';

const STYLED_COLUMNS = {
  NESTING_ROLE: 'Nesting Role',
  PERSONALIZED: 'Personalized',
};

const warehouseOptions = [
  { label: 'All Warehouses', value: '' },
  { label: 'EWE Melbourne', value: 'EWE' },
  { label: 'EWE Sydney', value: 'EWE - Sydney' },
  { label: 'EWE Brisbane', value: 'EWE - Brisbane' },
  { label: 'EWE Perth', value: 'EWE - Perth' },
  { label: 'Airbox UK', value: 'Airbox UK' },
  { label: 'UNIS', value: 'UNIS' },
  { label: 'Launch Utah', value: 'LCHSLC' },
  { label: 'Launch Kentucky', value: 'LCHKTC' },
];

const NestedOrders = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default to last 7 days
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(warehouseOptions[0]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `${backendUrl}/orders/export-nesting-instructions?startDate=${startDate}&endDate=${endDate}`;
      if (selectedWarehouse?.value) {
        url += `&warehouse=${encodeURIComponent(selectedWarehouse.value)}`;
      }

      const response = await superagent
        .get(url)
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');

      setData(response.body);
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        window.location.href = '/login';
        return;
      }
      setError(err.message || 'Failed to load nesting instructions');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const isDateRangeValid = startDate && endDate && startDate <= endDate;

  const handleFilter = () => {
    if (!isDateRangeValid) return;
    fetchData();
  };

  const setDateRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  // Get headers and data rows from the response
  const headers = data?.lines?.[0] || [];
  const rows = data?.lines?.slice(1) || [];

  const renderHeader = () => (
    <div className="mb-6">
      <Row className="flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <Col className="w-full lg:w-1/3">
          <h2 className="text-2xl font-bold text-gray-800">Nested Orders Report</h2>
          <CardDescription className="mt-2">
            Export nesting instructions for 3PL verification
          </CardDescription>
        </Col>
        <Col className="w-full lg:w-2/3">
          <div className="flex flex-col sm:flex-row gap-2 items-end">
            <div className="w-full sm:w-auto flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="form-control w-full"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-auto flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                className="form-control w-full"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-auto flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warehouse
              </label>
              <Select
                value={selectedWarehouse}
                onChange={setSelectedWarehouse}
                options={warehouseOptions}
                className="min-w-[180px]"
              />
            </div>
            <div className="w-full sm:w-auto">
              <label className="block text-sm font-medium text-transparent mb-1">Actions</label>
              <div className="flex gap-2">
                <Button
                  className="btn btn-primary h-[38px]"
                  onClick={handleFilter}
                  disabled={loading || !isDateRangeValid}
                >
                  {loading ? 'Loading...' : 'Apply Filter'}
                </Button>
                {data && rows.length > 0 && (
                  <CSVLink
                    data={data.lines}
                    filename={`nesting-instructions-${startDate}-to-${endDate}.csv`}
                  >
                    <Button className="bg-green-600 text-white hover:bg-green-700 h-[38px]">
                      Export CSV
                    </Button>
                  </CSVLink>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={() => setDateRange(7)}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border bg-background hover:text-accent-foreground h-8 rounded-md px-3 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            >
              Last 7 Days
            </button>
            <button
              type="button"
              onClick={() => setDateRange(30)}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border bg-background hover:text-accent-foreground h-8 rounded-md px-3 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            >
              Last 30 Days
            </button>
            <button
              type="button"
              onClick={() => setDateRange(90)}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border bg-background hover:text-accent-foreground h-8 rounded-md px-3 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            >
              Last 90 Days
            </button>
          </div>
          {!isDateRangeValid && startDate && endDate && (
            <p className="text-red-500 text-sm mt-1">Start date must be before or equal to end date.</p>
          )}
        </Col>
      </Row>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Loading...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-5">
          <h4>Error loading data</h4>
          <p className="text-danger">{error}</p>
          <Button
            className="btn btn-primary mt-3"
            onClick={handleFilter}
          >
            Try Again
          </Button>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="text-center p-5">
          <h4>Select a date range</h4>
          <p>Choose start and end dates, then click "Apply Filter" to load nesting instructions.</p>
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className="text-center p-5">
          <h4>No data found</h4>
          <p>No nesting instructions found for the selected date range and warehouse.</p>
        </div>
      );
    }

    return (
      <>
        <div className="mb-4">
          <Card className="mini-stats-wid">
            <CardBody className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-gray-700 text-sm">Total Records:</span>
                  <span className="font-medium text-gray-900">{data.count || rows.length}</span>
                </div>
                <div className="text-sm text-muted">
                  {startDate} to {endDate}
                  {selectedWarehouse?.value && ` | ${selectedWarehouse.value}`}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardBody className="pt-4">
            <div className="overflow-x-auto">
              <table className="table table-centered table-nowrap mb-0 min-w-full">
                <thead className="table-light">
                  <tr>
                    {headers.map((header, idx) => (
                      <th key={idx} className="py-3 text-sm">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className="py-2 text-sm">
                          {headers[cellIdx] === STYLED_COLUMNS.NESTING_ROLE ? (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              cell === 'Container'
                                ? 'bg-blue-100 text-blue-800'
                                : cell === 'Nested Item'
                                  ? 'bg-cyan-100 text-cyan-800'
                                  : 'bg-gray-100 text-gray-700'
                            }`}>
                              {cell}
                            </span>
                          ) : headers[cellIdx] === STYLED_COLUMNS.PERSONALIZED ? (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              cell === 'Yes'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {cell}
                            </span>
                          ) : (
                            cell
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="w-full px-3 mx-auto xl:max-w-[85%]">
          <Breadcrumb title="Operations" breadcrumbItem="Nested Orders Report" />
          {renderHeader()}
          {renderContent()}
        </div>
      </div>
    </React.Fragment>
  );
};

export default NestedOrders;
